import { Stack, Typography, useTheme } from "@mui/material";
import _ from "lodash";
import { useCallback, useMemo } from "react";
import { Accordion, AccordionTitle } from "src/components";
import { UserFeedbackFilterByOption, UserFeedbackGroupByOption } from "src/constants";
import { type FormSubmissionDto } from "src/http";
import { UserFeedbackFilterButton } from "./UserFeedbackFilterButton";
import { UserFeedbackListItem } from "./UserFeedbackListItem";
import { UserFeedbackListSkeleton } from "./UserFeedbackListSkeleton";

interface IProps {
  title: string;
  unfilteredItems: FormSubmissionDto[];
  filteredItems: FormSubmissionDto[];
  groupBy: UserFeedbackGroupByOption;
  filters: Record<UserFeedbackFilterByOption, string[]>;
  enableFilterBySender?: boolean;
  enableFilterByRecipient?: boolean;
  onGroupByChanged: (value: UserFeedbackGroupByOption) => void;
  onFilterByChanged: (filters: Record<UserFeedbackFilterByOption, string[]>) => void;
  onClicked?: (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>, formSubmissionGuid: string) => void;
  onDraftClicked?: (formSubmissionGuid: string, formGuid: string) => void;
}

const UserFeedbackList = (props: IProps) => {
  const theme = useTheme();

  const groupByFunction = useCallback(
    (item: FormSubmissionDto) => {
      switch (props.groupBy) {
        case UserFeedbackGroupByOption.None: {
          return "All";
        }
        case UserFeedbackGroupByOption.Type: {
          return item.formName;
        }
        case UserFeedbackGroupByOption.Sender: {
          return item.createdByUser.displayName;
        }
        case UserFeedbackGroupByOption.State: {
          return item.submittedDateTimeUtc == null ? "Draft" : "Submitted";
        }
      }
    },
    [props.groupBy],
  );

  const groupSortByFunction = useCallback((groupName: string) => {
    return groupName;
  }, []);

  const groupedItems = useMemo<Map<string, FormSubmissionDto[]> | undefined>(() => {
    if (props.filteredItems === undefined) {
      return undefined;
    }

    if (props.filteredItems !== undefined) {
      const groupedItems = _.groupBy(props.filteredItems, groupByFunction);
      const sortedGroupNames = _.sortBy(_.keys(groupedItems), groupSortByFunction);
      const map = sortedGroupNames.reduce((groupedSortedItems, groupName) => {
        groupedSortedItems.set(groupName, groupedItems[groupName]);
        return groupedSortedItems;
      }, new Map());

      return map;
    }
  }, [groupByFunction, groupSortByFunction, props.filteredItems]);

  const sortByFunction = useCallback((a: FormSubmissionDto, b: FormSubmissionDto): number => {
    const aSubmitted = a.submittedDateTimeUtc ?? "";
    const bSubmitted = b.submittedDateTimeUtc ?? "";

    if (aSubmitted !== bSubmitted) {
      if (aSubmitted === "") return 1;
      if (bSubmitted === "") return -1;

      return bSubmitted.localeCompare(aSubmitted);
    }

    return b.createdDateTimeUtc.localeCompare(a.createdDateTimeUtc);
  }, []);

  if (groupedItems === undefined) {
    return <UserFeedbackListSkeleton />;
  }

  return (
    <Stack spacing={0.5} sx={{ p: 0.5 }}>
      <Stack direction="row" spacing={1} sx={{ justifyContent: "space-between" }}>
        <Typography variant="h6" sx={{ pl: 0.5 }}>
          {props.title}
        </Typography>
        <UserFeedbackFilterButton
          items={props.unfilteredItems}
          groupBy={props.groupBy}
          filters={props.filters}
          onGroupByChanged={props.onGroupByChanged}
          onFilterByChanged={props.onFilterByChanged}
          enableFilterByReceiver={props.enableFilterByRecipient}
          enableFilterBySender={props.enableFilterBySender}
        />
      </Stack>
      <Stack spacing={1}>
        {groupedItems.size === 0 ? (
          <Typography variant="placeholder" sx={{ px: 0.5 }}>
            Nothing here yet.
          </Typography>
        ) : (
          Array.from(groupedItems.keys()).map((groupName, i) => (
            <Accordion key={i} title={<AccordionTitle title={groupName} />} backgroundColor={theme.palette.grey[100]}>
              <Stack spacing={0.5} sx={{ px: 0.5, pb: 0.5 }}>
                {(groupedItems.get(groupName) ?? []).sort(sortByFunction).map((formSubmission, i) => (
                  <UserFeedbackListItem
                    key={i}
                    formSubmission={formSubmission}
                    onClicked={props.onClicked}
                    onDraftClicked={props.onDraftClicked}
                  />
                ))}
              </Stack>
            </Accordion>
          ))
        )}
      </Stack>
    </Stack>
  );
};

export { UserFeedbackList };
